import React from 'react';
import ReactDOM from 'react-dom';
import {debounce} from 'throttle-debounce';
import Grid from '@material-ui/core/Grid';
import Modal from '@material-ui/core/Modal';
import MotionController from '../motionController/motionController';
import MotionGallery from '../motionGallery/motionGallery';
import MotionView from '../motionView/motionView';
import ExportCode from '../exportCode/exportCode';
import {galleryPresets} from '../../data/gallery';
import {DEFAULT_COPY, DEFAULT_FONT_OPTIONS, DEFAULT_TRANSITION_OPTIONS, DEFAULT_BACKGROUND_OPTIONS} from '../../data/defaults';
import {shallowOptionsDiff, getParameterByName, hasLocalStorage, scrollToTop } from '../../utils';
import './motionHelper.scss';

const NO_UPDATE_KEYS = [ // List of properties that shouldn't trigger an animation rerender
	'characterWordSpacing'
];

export default class MotionHelper extends React.Component {
  constructor() {
    super();
    this.state = {
      headerDOMAvailable: false,
      showGallery: false,
      showController: false,
      showExport: false,
      backgroundOptions: DEFAULT_BACKGROUND_OPTIONS,
      transitionOptions: DEFAULT_TRANSITION_OPTIONS,
      copy: DEFAULT_COPY,
      fontOptions: DEFAULT_FONT_OPTIONS,
      canSave: false, // if changes have been made, allow to save options in local storage
      history: {},
      savedSnapshots: {},
      lastUpdate: Date.now(),
    };    

    this.headerTitleContainer = null;
    this.headerSublinkWrapper = null;    
  }

  componentDidMount() {
    this.headerTitleContainer = document.getElementById('header-title-container');
    this.headerSublinkWrapper = document.getElementById('header-sublink-wrapper');
    this.setState({headerDOMAvailable: true});
    this.debouncedSetQueryUrl = debounce(300, this.setQueryUrl);
    this.restoreSavedSnapshots();
    this.setInitialOptionsFromQuery();
  }

  componentWillUpdate() {
    if(this.debouncedSetQueryUrl) {
      this.debouncedSetQueryUrl();
    }
  }

  setInitialOptionsFromQuery = () => {
    const {transitionOptions, fontOptions, backgroundOptions, copy} = this.state;
    const options = getParameterByName('edit', window.location);    
  
    if(options) {
      const queryOptions = JSON.parse(decodeURIComponent(options));
      const newTransitionOptions = Object.assign({}, transitionOptions, queryOptions.transition);
      const newFontOptions = Object.assign({}, fontOptions, queryOptions.font);
      const newBackgroundOptions = Object.assign({}, backgroundOptions, queryOptions.background);
      const newCopy = queryOptions.copy ? queryOptions.copy : copy;
      this.setState({copy: newCopy, transitionOptions: newTransitionOptions, fontOptions: newFontOptions, backgroundOptions: newBackgroundOptions});
    }
  }

  setQueryUrl = () => {
    const {transitionOptions, fontOptions, backgroundOptions, copy} = this.state;
    let transitionChanges = shallowOptionsDiff(transitionOptions, DEFAULT_TRANSITION_OPTIONS);
    let fontChanges = shallowOptionsDiff(fontOptions, DEFAULT_FONT_OPTIONS);
    let backgroundChanges = shallowOptionsDiff(backgroundOptions, DEFAULT_BACKGROUND_OPTIONS);

    let shareParams = {
      transition: transitionChanges,
      font: fontChanges,
      background: backgroundChanges,
    };

    if(copy !== DEFAULT_COPY) {
      shareParams.copy = copy;
    }

    if (window && window.history) {
      const newUrl = `${window.location.protocol}//${window.location.host}${
        window.location.pathname
      }?edit=${encodeURIComponent(JSON.stringify(shareParams))}`;
      window.history.replaceState({path: newUrl}, "", newUrl);
    }
  }

  onAnimationRefresh = () => {
    this.setState({lastUpdate: Date.now()});
  }

  onCopyChange = e => {
    const newCopy = e.currentTarget.value;
    this.setState({ copy: newCopy });    
  }

  onGalleryItemSelection = (optionIndex) => {
    scrollToTop();
    this.setState({transitionOptions: galleryPresets[optionIndex], showGallery: false});
  }

  onGallerySnapshotSelection = (optionIndex) => {
    scrollToTop();
    const {savedSnapshots} = this.state;
    const savedDate = Object.keys(savedSnapshots)[optionIndex]
    this.setState({transitionOptions: savedSnapshots[savedDate].transitionOptions, showGallery: false});
  }

  onTransitionOptionsChange = (newValue, transitionOptionsKey) => {
		const { transitionOptions } = this.state;
		const shouldRerender = !NO_UPDATE_KEYS.includes(transitionOptionsKey);
		 
    const updatedTransitionOptions = {
      ...transitionOptions,
      [transitionOptionsKey]: newValue,
      lastUpdate: Date.now(),
    }
    this.setState({
      transitionOptions: updatedTransitionOptions,
      lastUpdate: shouldRerender ? Date.now() : this.state.lastUpdate,
      canSave: true,
    })
  }

  onFontOptionsChange = (newValue, fontOptionKey) => {
    const { fontOptions } = this.state;
    
    let updatedFontOptions = {
      ...fontOptions,
      [fontOptionKey]: newValue,
      lastUpdate: Date.now(),
      canSave: true,
    };

    // Make sure the custom font is reset back to null when we pick a font from our default list.
  
    if(fontOptionKey === 'family') {
      updatedFontOptions.customFont = null; 
    }

    this.setState({ fontOptions: updatedFontOptions, canSave: true });

  }

  onBackgroundOptionsChange = (newValue, backgroundOptionsKey) => {
		
		const { backgroundOptions } = this.state;
		
    const updatedBackgroundOptions = {
      ...backgroundOptions,
      [backgroundOptionsKey]: newValue,
      lastUpdate: Date.now(),
		}
		
		if (backgroundOptionsKey === 'bgRandomTimestamp') {
			updatedBackgroundOptions.bgRandomTimestamp = `https://picsum.photos/1500/768?timestamp=${newValue}`;
    }

    this.setState({
      backgroundOptions: updatedBackgroundOptions,
      lastUpdate: Date.now(),
      canSave: true,
    });
  }

  restoreSavedSnapshots = () => {
    if(hasLocalStorage()) {
      const key = 'motionlab__saved-snapshots';
      let previousSaveOptions = localStorage.getItem(key);
      if(previousSaveOptions) {
        this.setState({ savedSnapshots: JSON.parse(previousSaveOptions) });
      }
    }
  }

  removeSnapshot = (optionIndex) => {
    const {savedSnapshots} = this.state;
    const newSnapshotList = Object.assign({}, savedSnapshots);
    const keyToDelete = Object.keys(savedSnapshots)[optionIndex];
    delete newSnapshotList[keyToDelete];
    if(hasLocalStorage()) {
      const key = 'motionlab__saved-snapshots';    
      localStorage.setItem(key, JSON.stringify(newSnapshotList));
    }
    this.setState({savedSnapshots: newSnapshotList});
  }

  saveSnapshot = () => {
    const {transitionOptions, fontOptions, backgroundOptions} = this.state;
    
    const currentOptions = {};
    currentOptions.transitionOptions = transitionOptions;
    currentOptions.fontOptions = fontOptions;
    currentOptions.backgroundOptions = backgroundOptions;
    let updatedSnapshots = {};
    
    if(hasLocalStorage()) {
      const key = 'motionlab__saved-snapshots';
      const savedSnapshots = localStorage.getItem(key);
      if(savedSnapshots) {
        updatedSnapshots = JSON.parse(savedSnapshots);
      }
      
      updatedSnapshots[Date.now()] = currentOptions;
      localStorage.setItem(key, JSON.stringify(updatedSnapshots));
    }
    
    this.setState({ savedSnapshots: updatedSnapshots, canSave: false });
  }

  onShowControllerChange = () => {
    const {showController} = this.state;
    this.setState({showController: !showController});
  }

  onGalleryModeChange = () => {
    const {showGallery} = this.state;
    this.setState({showGallery: !showGallery});
  }

  onExport = () => {
    this.setState({showExport: true});
  }

  render() {
    const {
      showGallery,
      showController,
      showExport,
      backgroundOptions,
      transitionOptions,
      copy,
      fontOptions,
      lastUpdate,
      savedSnapshots,
      canSave,
    } = this.state;

    return (
      <div className='motion-helper-wrapper'>

        {/* Home Btn */}
        {this.headerSublinkWrapper && ReactDOM.createPortal(
          <h1 className="header-title" onClick={() => this.setState({showGallery: false, showController: false})}>Motion Lab</h1>
        , this.headerTitleContainer)}

        {/* Save Btn */}
        {this.headerSublinkWrapper && ReactDOM.createPortal(
          <h4 className={`save-btn ${canSave ? 'visible' : ''}`} onClick={this.saveSnapshot}>Save</h4>
        , this.headerSublinkWrapper)}

        {/* Export Btn */}
        {this.headerSublinkWrapper && ReactDOM.createPortal(
          <h4 className={`gallery-btn ${canSave ? 'visible' : ''}`} onClick={this.onExport}>Export</h4>
        , this.headerSublinkWrapper)}

        {/* Gallery Btn */}
        {this.headerSublinkWrapper && ReactDOM.createPortal(
          <h4 className={`gallery-btn ${showGallery ? 'active' : ''}`} onClick={() => this.setState({showGallery: !showGallery})}>Gallery</h4>
        , this.headerSublinkWrapper)}

        {/* Toolbar Btn */}
        {this.headerSublinkWrapper && ReactDOM.createPortal(
          <h4 className={`toolbar-btn ${showController ? 'active' : ''}`} onClick={() => this.setState({showController: !showController})}>Tools</h4>
        , this.headerSublinkWrapper)}

        <Grid
          container
          direction="row"
          justify="space-between"
          alignItems="flex-start"
        >

        {showExport && <Modal
          aria-labelledby="simple-modal-title"
          aria-describedby="simple-modal-description"
          open={showExport}
          onClose={() => this.setState({showExport: false})}
        >
          <div className="modal">
            <ExportCode
              copy={copy}              
              transitionOptions={transitionOptions}
            />
          </div>
        </Modal>}

          {showGallery && <Grid item lg={12}>
            <MotionGallery 
              key={lastUpdate}
              copy={copy} 
              fontOptions={fontOptions}
              backgroundOptions={backgroundOptions}
              onItemClick={this.onGalleryItemSelection}
              onSnapshotClick={this.onGallerySnapshotSelection}
              onGalleryModeChange={this.onGalleryModeChange}
              onShowControllerChange={this.onShowControllerChange}
              onRemoveSnapshot={this.removeSnapshot}
              savedSnapshots={savedSnapshots}
            />
          </Grid>}

          {!showGallery &&<Grid item lg={12}>
            <MotionView
              key={lastUpdate}
              defaultValue={DEFAULT_COPY}
              copy={copy}
              transitionOptions={transitionOptions}
              fontOptions={fontOptions}
              backgroundOptions={backgroundOptions}
              onCopyChange={this.onCopyChange}
              onAnimationRefresh={this.onAnimationRefresh}
              onShowControllerChange={this.onShowControllerChange}
              onGalleryModeChange={this.onGalleryModeChange}
            />
          </Grid>}

          <div className={`motion-controller-wrapper ${showController ? 'visible' : ''}`}>
            <Grid item lg={12}>
              <MotionController
                galleryMode={showGallery}
                transitionOptions={transitionOptions}
                onTransitionOptionsChange={this.onTransitionOptionsChange}
                fontOptions={fontOptions}
                onFontOptionsChange={this.onFontOptionsChange}
                backgroundOptions={backgroundOptions}
                onBackgroundOptionsChange={this.onBackgroundOptionsChange}
              />
            </Grid>
          </div>
        </Grid>
      </div>
    )
  }
}
